import React, { useMemo, useRef } from 'react';
import { useAppSelector } from 'hooks';

import { Row, TableView, TableViewHandle } from '~components/table-view-v3';

import { COPY_STATUS, Section, SectionItem } from '~support-at-home/models';

import { columnsFactory } from '~support-at-home/forms/budgets/sectionItemsList';

import { selectLookupCalendarFrequencies } from '~lookups/selectors/calendarFrequencies';
import { selectLookupShifts } from '~lookups/selectors/shifts';

import SectionTotal from './BudgetSectionTotal';

interface ComponentProps {
  id: string;
  section: Section;
  initialValues?: Section;
  start_date?: string;
  end_date?: string;
  filteredServices: any[];
  readonly: boolean;
  /** Upstream callbacks for WizardForm-managed data */
  onUpdate: (sectionId: string, items: SectionItem[]) => void;
  onDelete?: (sectionId: string, item: SectionItem) => void;
}

const BudgetSection: React.FC<ComponentProps> = React.memo(
  ({
    id,
    section,
    initialValues,
    start_date = '',
    end_date = '',
    filteredServices,
    readonly = false,
    onUpdate,
    onDelete,
  }) => {
    const calendarFrequencies = useAppSelector(selectLookupCalendarFrequencies);
    const shifts = useAppSelector(selectLookupShifts);

    const { category_id, category, items, contrib_proportion } = section;

    const tableRef = useRef<TableViewHandle>(null);
    const columns = useMemo(() => {
      return columnsFactory(shifts, calendarFrequencies, filteredServices, initialValues);
    }, [shifts, calendarFrequencies, filteredServices, initialValues]);

    const handleDelete = (rowId: string | number) => {
      const row = items.find(({ id }) => id === rowId);
      if (row) {
        onDelete?.(category_id, row);
      }
    };

    const handleChange = (allRows: Row[]) => {
      const updatedRows = allRows.map((row) => ({
        ...row,
        category_id: row.category_id ?? category_id,
        start_date: row.start_date ?? start_date,
        end_date: row.end_date ?? end_date,
      })) as SectionItem[];

      onUpdate?.(category_id, updatedRows);
    };

    // Only check for changes if we have initial values, if not "its a new budget"
    const contributionChanged = initialValues && initialValues?.contrib_proportion !== contrib_proportion;

    return (
      <div key={id}>
        <div className="budget-section-container">
          <div className="section-header">
            <div>
              <strong>Category:</strong> {category?.name}
            </div>
            <div className={`p-2 rounded ${contributionChanged ? 'bg-warning' : ''}`}>
              <strong>Co-Contribution:</strong> {contrib_proportion}%
            </div>
          </div>
          <TableView
            ref={tableRef}
            id={category_id}
            data={items}
            columns={columns}
            context={{ category_id, start_date, end_date, initialRows: initialValues?.items || [] }}
            onChange={handleChange}
            onDeleteRow={handleDelete}
            isRowDisabled={(row) => row.copy_status === COPY_STATUS.REMOVED}
            canDeleteRow={(row) => row.copy_status !== COPY_STATUS.REMOVED}
            emptyMessage="No items found"
            readonly={readonly}
            editonly
            compactView
            bordered
          />
        </div>
        <div className="section-totals-container">
          <SectionTotal category_id={category_id} section={section} />
        </div>
      </div>
    );
  },
);

BudgetSection.displayName = 'BudgetSection';
export default BudgetSection;
